import { SplitSupplyForm } from '@ebalance/proto/bffuser/v1/split_supply_contract_model_pb'
import i18next from 'i18next'
import { z } from 'zod'

import type { BasePatternValue } from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/models/basePattern'
import { withinRunes } from '~/pages/AdminOcctoMaster/tabs/_common/schema'

/** 契約識別番号 1 の上限 (BP 標準 JP06351 / JP06354 X(20))。 */
export const CONTRACT_ID_MAX_RUNES = 20

/** 備考の上限 (他の OCCTO マスタの備考と同じ 100 文字。 proto の max_len と同じ値)。 */
export const NOTE_MAX_RUNES = 100

/** 1 契約の基本パターンの上限 (proto の max_items と同じ値)。 */
export const BASE_PATTERNS_MAX_COUNT = 100

/** 自社の立場 ('' = 未選択)。 画面だけの値で、 保存されるのは両側の事業者。 */
export type SelfRoleChoice = 'loadFollowing' | 'base' | ''

/**
 * 変動型の「通告値を出す側」を画面では 自社 / 他社 で選ぶ ('' = 未選択)。 どちらが出すかに
 * 決まりは無いので既定は無く、 変動型では必ず選ぶ。 保存されるのはベース分 / 負荷追随分で、
 * 自社の立場との組み合わせで置き換える (`noticeBySide` / `noticeByChoiceOf`)。
 * 立場とは別の項目 (託送供給等約款の申込み事項)。
 */
export type NoticeByChoice = 'self' | 'other' | ''

/**
 * 自社 / 他社 と自社の立場から、 保存する「通告値を出す側」 (ベース分 / 負荷追随分) を決める。
 * 未選択・立場が未選択なら undefined。
 */
export const noticeBySide = (
  noticeBy: NoticeByChoice,
  selfRole: SelfRoleChoice,
): 'base' | 'loadFollowing' | undefined => {
  if (noticeBy === '' || selfRole === '') return undefined
  return (noticeBy === 'self') === (selfRole === 'base')
    ? 'base'
    : 'loadFollowing'
}

/** 保存済みの「通告値を出す側」から、 自社 / 他社 を出す (編集で開いた時)。 */
export const noticeByChoiceOf = (
  side: 'base' | 'loadFollowing',
  selfRole: SelfRoleChoice,
): NoticeByChoice => {
  if (selfRole === '') return ''
  return (side === 'base') === (selfRole === 'base') ? 'self' : 'other'
}

/**
 * ベース分供給者の契約電力 (kW) の入力が 1 以上の整数か (kW に小数は無く、 最小は 1 kW。
 * spec BaseContractKw「整数のみ、1 以上」、 ドメインエキスパート承認 2026-10-29)。
 */
export const isWholeKwOfOneOrMore = (value: string): boolean => {
  const trimmed = value.trim()
  return /^\d+$/.test(trimmed) && Number(trimmed) >= 1
}

/** 契約電力の入力が小数 (例 1250.5) か。 小数だけは「小数を含みます」の理由を出す。 */
const isDecimalKw = (value: string): boolean => /^\d+\.\d+$/.test(value.trim())

/**
 * 分割供給契約フォームの値。 入力欄に合わせて数値は文字列のまま持ち、 送信時に変換する。
 * 業務の判断 (立場・候補・適格性・基本パターン) の正は backend で、 ここの検査は
 * 入力途中に理由を出すための事前チェック (backend は同じ検査を API 直叩きにも掛ける)。
 */
export type SplitSupplyFormValues = {
  supplyPointSpecificNumber: string
  selfRole: SelfRoleChoice
  loadFollowingBusinessMasterId: string
  loadFollowingContractId: string
  baseBusinessMasterId: string
  baseContractId: string
  basePriority: string
  supplyForm: SplitSupplyForm
  ownSlotFrom: string
  ownSlotTo: string
  /** 変動型だけ送る (自社 / 他社)。 変動型は必須。 */
  noticeBy: NoticeByChoice
  /** 固定型だけ送る (必須)。 入力途中の文字列のまま持つ。 */
  baseContractKw: string
  note: string
  basePatterns: BasePatternValue[]
}

const msg = (key: string) => (): string =>
  i18next.t(`page:adminOcctoMaster.splitSupplyContract.validation.${key}`)

/**
 * 契約識別番号 1 は両側とも全形態で必須 (通告値 XML の JP06351 / JP06354)。
 * 空欄・空白だけは側ごとの文言で止める。
 */
const contractIdSchema = (requiredKey: string): z.ZodString =>
  z
    .string()
    .refine((v) => v.trim() !== '', { error: msg(requiredKey) })
    .refine(withinRunes(CONTRACT_ID_MAX_RUNES), {
      error: msg('contractIdTooLong'),
    })
    // OCCTO XSD STR20S: 印字できる半角 (ASCII 0x20〜0x7E) だけ
    .refine((v) => /^[\x20-\x7e]*$/.test(v), {
      error: msg('contractIdNotHalfWidth'),
    })

/** モジュール scope で組み立てるため文言は `i18next.t` を関数で渡して parse 時に引く。 */
export const splitSupplyFormSchema = z
  .object({
    supplyPointSpecificNumber: z
      .string()
      .min(1, { error: msg('supplyPointRequired') }),
    selfRole: z.enum(['loadFollowing', 'base'], {
      error: msg('selfRoleRequired'),
    }),
    loadFollowingBusinessMasterId: z
      .string()
      .min(1, { error: msg('loadFollowingRequired') }),
    loadFollowingContractId: contractIdSchema(
      'loadFollowingContractIdRequired',
    ),
    baseBusinessMasterId: z.string().min(1, { error: msg('baseRequired') }),
    baseContractId: contractIdSchema('baseContractIdRequired'),
    basePriority: z
      .string()
      .regex(/^([1-9])?$/, { error: msg('priorityInvalid') }),
    supplyForm: z
      .enum(SplitSupplyForm)
      .refine((v) => v !== SplitSupplyForm.UNSPECIFIED, {
        error: msg('supplyFormRequired'),
      }),
    ownSlotFrom: z.string(),
    ownSlotTo: z.string(),
    noticeBy: z.enum(['self', 'other', '']),
    baseContractKw: z.string(),
    note: z
      .string()
      .refine(withinRunes(NOTE_MAX_RUNES), { error: msg('noteTooLong') }),
    basePatterns: z
      .array(z.custom<BasePatternValue>())
      .max(BASE_PATTERNS_MAX_COUNT, { error: msg('basePatternsTooMany') }),
  })
  .superRefine((value, ctx) => {
    if (
      value.supplyForm === SplitSupplyForm.VARIABLE &&
      value.noticeBy === ''
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['noticeBy'],
        message: i18next.t(
          'page:adminOcctoMaster.splitSupplyContract.validation.noticeByRequired',
        ),
      })
    }
    if (
      value.supplyForm === SplitSupplyForm.FIXED &&
      !isWholeKwOfOneOrMore(value.baseContractKw)
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['baseContractKw'],
        message: isDecimalKw(value.baseContractKw)
          ? i18next.t(
              'page:adminOcctoMaster.splitSupplyContract.validation.baseContractKwDecimal',
              { raw: value.baseContractKw.trim() },
            )
          : i18next.t(
              'page:adminOcctoMaster.splitSupplyContract.validation.baseContractKwRequired',
            ),
      })
    }
    if (value.supplyForm !== SplitSupplyForm.TIME_SPLIT) return
    const from =
      value.ownSlotFrom === '' ? undefined : Number(value.ownSlotFrom)
    const to = value.ownSlotTo === '' ? undefined : Number(value.ownSlotTo)
    // 開始・終了はコマ番号 (終了コマの終わりが終了時刻) なので、 from > to が「終了 ≦ 開始」
    if (from === undefined || to === undefined || from > to) {
      ctx.addIssue({
        code: 'custom',
        path: ['ownSlotTo'],
        message: i18next.t(
          from === undefined || to === undefined
            ? 'page:adminOcctoMaster.splitSupplyContract.validation.ownSlotRequired'
            : 'page:adminOcctoMaster.splitSupplyContract.validation.ownSlotOrder',
        ),
      })
    }
  })
